// ============================================
// SettingsPage.jsx - PROFESSIONAL VERSION
// No emojis - proper Lucide icons only
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import {
  User, Lock, Bell, Eye, Coins, Share2, Globe,
  Moon, Sun, Monitor, Trash2, Download, Copy,
  CheckCircle, AlertTriangle, ChevronRight, ChevronLeft,
  Camera, Shield, Palette, Settings, Star,
  LogOut, ExternalLink, BookOpen, Award,
  CreditCard, Key, Sliders, BarChart2,
  ToggleLeft, ToggleRight, Save, ArrowLeft,
  UserX, RefreshCw, Database, Link
} from 'lucide-react';

const SettingsPage = ({
  currentUser,
  setCurrentUser,
  updateUser,
  signOutUser,
  setView,
  setShowSubscriptionModal,
}) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(true);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('vibelyDarkMode') === 'true'
  );
  const [accentColor, setAccentColor] = useState(
    localStorage.getItem('vibelyAccentColor') || 'indigo'
  );
  const [fontSize, setFontSize] = useState(
    localStorage.getItem('vibelyFontSize') || 'medium'
  );
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const [profileForm, setProfileForm] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    country: currentUser?.country || 'US',
    bio: currentUser?.bio || '',
    penName: currentUser?.penName || '',
    amazonReviewerName: currentUser?.amazonReviewerName || '',
    profilePhoto: currentUser?.profilePhoto || ''
  });

  const [notifications, setNotifications] = useState({
    bookClaimed: currentUser?.notificationPrefs?.bookClaimed ?? true,
    reviewApproved: currentUser?.notificationPrefs?.reviewApproved ?? true,
    reviewRejected: currentUser?.notificationPrefs?.reviewRejected ?? true,
    lowCoins: currentUser?.notificationPrefs?.lowCoins ?? true,
    weeklyLimit: currentUser?.notificationPrefs?.weeklyLimit ?? true,
    emailNotifications: currentUser?.notificationPrefs?.emailNotifications ?? false,
  });

  const [privacy, setPrivacy] = useState({
    profilePublic: currentUser?.privacy?.profilePublic ?? true,
    showReviewHistory: currentUser?.privacy?.showReviewHistory ?? true,
    showCoinBalance: currentUser?.privacy?.showCoinBalance ?? false,
  });

  const [preferences, setPreferences] = useState({
    defaultMarketplace: currentUser?.preferences?.defaultMarketplace || 'US',
    defaultGenre: currentUser?.preferences?.defaultGenre || 'All',
    reminderDays: currentUser?.preferences?.reminderDays || 2,
    language: currentUser?.preferences?.language || 'English',
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('vibelyDarkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('vibelyDarkMode', 'false');
    }
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.style.fontSize =
      fontSize === 'small' ? '14px' :
      fontSize === 'large' ? '18px' : '16px';
    localStorage.setItem('vibelyFontSize', fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('vibelyAccentColor', accentColor);
  }, [accentColor]);

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    await updateUser(currentUser.id, {
      name: profileForm.name,
      country: profileForm.country,
      bio: profileForm.bio,
      penName: profileForm.penName,
      amazonReviewerName: profileForm.amazonReviewerName,
      profilePhoto: profileForm.profilePhoto
    });
    setCurrentUser(prev => ({ ...prev, ...profileForm }));
    setSaving(false);
    showSaved();
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    await updateUser(currentUser.id, { notificationPrefs: notifications });
    setCurrentUser(prev => ({ ...prev, notificationPrefs: notifications }));
    setSaving(false);
    showSaved();
  };

  const handleSavePrivacy = async () => {
    setSaving(true);
    await updateUser(currentUser.id, { privacy });
    setCurrentUser(prev => ({ ...prev, privacy }));
    setSaving(false);
    showSaved();
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    await updateUser(currentUser.id, { preferences });
    setCurrentUser(prev => ({ ...prev, preferences }));
    setSaving(false);
    showSaved();
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      alert('Please upload a JPG, PNG, or GIF image');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileForm(prev => ({ ...prev, profilePhoto: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleExportData = () => {
    const data = {
      profile: {
        name: currentUser.name,
        email: currentUser.email,
        country: currentUser.country,
        coins: currentUser.coins,
        createdAt: currentUser.createdAt
      },
      subscription: {
        reviewPlan: currentUser.reviewSubscription,
        promotionPlan: currentUser.promotionSubscription
      },
      affiliate: {
        isAffiliate: currentUser.isAffiliate,
        referralCode: currentUser.referralCode
      }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vibely-data-${currentUser.name}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyReferral = () => {
    const link = `${window.location.origin}?ref=${currentUser.referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSectionSelect = (sectionId) => {
    setActiveSection(sectionId);
    setShowMobileMenu(false);
  };

  const navItems = [
    { id: 'profile',       label: 'Profile',       icon: User        },
    { id: 'security',      label: 'Security',      icon: Shield      },
    { id: 'appearance',    label: 'Appearance',    icon: Palette     },
    { id: 'notifications', label: 'Notifications', icon: Bell        },
    { id: 'subscription',  label: 'Subscription',  icon: CreditCard  },
    { id: 'privacy',       label: 'Privacy',       icon: Eye         },
    { id: 'affiliate',     label: 'Affiliate',     icon: Share2      },
    { id: 'preferences',   label: 'Preferences',   icon: Sliders     },
    { id: 'danger',        label: 'Danger Zone',   icon: AlertTriangle },
  ];

  const accentColors = [
    { name: 'indigo', hex: '#4F46E5', label: 'Indigo'  },
    { name: 'purple', hex: '#9333EA', label: 'Purple'  },
    { name: 'blue',   hex: '#2563EB', label: 'Blue'    },
    { name: 'green',  hex: '#16A34A', label: 'Green'   },
    { name: 'rose',   hex: '#E11D48', label: 'Rose'    },
    { name: 'amber',  hex: '#D97706', label: 'Amber'   },
  ];

  // Reusable Toggle Switch
  const Toggle = ({ value, onChange }) => (
    <button
      onClick={onChange}
      className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${value ? 'bg-indigo-600' : 'bg-gray-300'}`}
      role="switch"
      aria-checked={value}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${value ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );

  // Reusable Section Row
  const SettingRow = ({ icon: Icon, label, desc, children }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3 flex-1 min-w-0 mr-4">
        {Icon && <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-800">{label}</p>
          {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );

  // Section Header
  const SectionHeader = ({ icon: Icon, title, desc }) => (
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-indigo-600" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {desc && <p className="text-sm text-gray-500">{desc}</p>}
      </div>
    </div>
  );

  // Save Button
  const SaveButton = ({ onClick, saving, label = 'Save Changes' }) => (
    <button
      onClick={onClick}
      disabled={saving}
      className="mt-6 inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
    >
      <Save className="w-4 h-4" />
      {saving ? 'Saving...' : label}
    </button>
  );

  return (
    <div className="w-full">

      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-sm text-gray-500">Manage your profile and preferences</p>
          </div>
        </div>
        {saved && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-lg text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Changes saved
          </div>
        )}
      </div>

      {/* ── MOBILE: Menu List ── */}
      <div className="md:hidden">
        {showMobileMenu ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {navItems.map(({ id, label, icon: Icon }, index) => (
              <button
                key={id}
                onClick={() => handleSectionSelect(id)}
                className={`w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition ${
                  index < navItems.length - 1 ? 'border-b border-gray-100' : ''
                } ${id === 'danger' ? 'text-red-600' : 'text-gray-700'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    id === 'danger' ? 'bg-red-50' : 'bg-gray-100'
                  }`}>
                    <Icon className={`w-4 h-4 ${id === 'danger' ? 'text-red-500' : 'text-gray-600'}`} />
                  </div>
                  <span className="font-medium text-sm">{label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
        ) : (
          <button
            onClick={() => setShowMobileMenu(true)}
            className="flex items-center gap-2 text-indigo-600 mb-4 font-medium text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </button>
        )}
      </div>

      {/* ── LAYOUT: Desktop side-by-side / Mobile content only ── */}
      <div className={`${showMobileMenu ? 'hidden md:flex' : 'flex'} gap-6 items-start`}>

        {/* ── Desktop Sidebar ── */}
        <aside className="hidden md:block w-52 flex-shrink-0">
          <nav className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {navItems.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition border-b border-gray-100 last:border-0 ${
                  activeSection === id
                    ? 'bg-indigo-50 text-indigo-700 border-l-4 border-l-indigo-600'
                    : id === 'danger'
                    ? 'text-red-500 hover:bg-red-50'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
                {activeSection === id && <ChevronRight className="w-3 h-3 ml-auto" />}
              </button>
            ))}
          </nav>
        </aside>

        {/* ── Main Content Panel ── */}
        <div className="flex-1 min-w-0">

          {/* ════════════ PROFILE ════════════ */}
          {activeSection === 'profile' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <SectionHeader icon={User} title="Profile" desc="Update your public profile information" />

              {/* Avatar */}
              <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-indigo-100 border-2 border-white shadow flex items-center justify-center">
                    {profileForm.profilePhoto ? (
                      <img src={profileForm.profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl font-bold text-indigo-600">
                        {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center hover:bg-indigo-700 transition shadow"
                  >
                    <Camera className="w-3 h-3" />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{currentUser?.name}</p>
                  <p className="text-sm text-gray-500 truncate">{currentUser?.email}</p>
                  <button onClick={() => fileInputRef.current?.click()} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-1">
                    Change photo
                  </button>
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Display Name</label>
                    <input
                      value={profileForm.name}
                      onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      placeholder="Your display name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Email Address</label>
                    <input
                      value={profileForm.email}
                      readOnly
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-400 mt-1">Contact support to change</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Author Pen Name</label>
                    <input
                      value={profileForm.penName}
                      onChange={e => setProfileForm(p => ({ ...p, penName: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      placeholder="Name used on Amazon books"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Amazon Reviewer Name</label>
                    <input
                      value={profileForm.amazonReviewerName}
                      onChange={e => setProfileForm(p => ({ ...p, amazonReviewerName: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      placeholder="Name on your Amazon reviews"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Country / Marketplace</label>
                  <select
                    value={profileForm.country}
                    onChange={e => setProfileForm(p => ({ ...p, country: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="US">United States (amazon.com)</option>
                    <option value="UK">United Kingdom (amazon.co.uk)</option>
                    <option value="CA">Canada (amazon.ca)</option>
                    <option value="AU">Australia (amazon.com.au)</option>
                    <option value="DE">Germany (amazon.de)</option>
                    <option value="FR">France (amazon.fr)</option>
                    <option value="IN">India (amazon.in)</option>
                    <option value="NG">Nigeria</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Bio</label>
                  <textarea
                    value={profileForm.bio}
                    onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                    placeholder="Tell other users about yourself..."
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{profileForm.bio.length} / 200</p>
                </div>
              </div>

              <SaveButton onClick={handleSaveProfile} saving={saving} label="Save Profile" />
            </div>
          )}

          {/* ════════════ SECURITY ════════════ */}
          {activeSection === 'security' && (
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <SectionHeader icon={Shield} title="Security" desc="Manage your account security settings" />

                {/* Card Verification */}
                <div className={`flex items-center gap-4 p-4 rounded-xl border-2 mb-4 ${currentUser?.cardVerified ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${currentUser?.cardVerified ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <CreditCard className={`w-5 h-5 ${currentUser?.cardVerified ? 'text-green-600' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm ${currentUser?.cardVerified ? 'text-green-800' : 'text-amber-800'}`}>
                      Card Verification — {currentUser?.cardVerified ? 'Verified' : 'Not Verified'}
                    </p>
                    <p className={`text-xs mt-0.5 ${currentUser?.cardVerified ? 'text-green-600' : 'text-amber-600'}`}>
                      {currentUser?.cardVerified
                        ? `Verified on ${new Date(currentUser.cardVerifiedAt).toLocaleDateString()}`
                        : 'Verify your card to unlock full access'}
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${currentUser?.cardVerified ? 'bg-green-500' : 'bg-amber-400'}`}>
                    {currentUser?.cardVerified
                      ? <CheckCircle className="w-4 h-4 text-white" />
                      : <AlertTriangle className="w-4 h-4 text-white" />
                    }
                  </div>
                </div>

                {/* Password */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <SettingRow icon={Key} label="Password" desc="Last changed: unknown">
                    <button
                      onClick={() => alert(`Password reset email sent to ${currentUser?.email}. Check your inbox.`)}
                      className="flex-shrink-0 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
                    >
                      Reset
                    </button>
                  </SettingRow>
                </div>

                {/* Account Info */}
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Account Details</p>
                  <div className="space-y-3">
                    {[
                      { label: 'User ID', value: `${currentUser?.id?.substring(0, 16)}...`, icon: Database },
                      {
                        label: 'Member Since',
                        value: currentUser?.createdAt
                          ? new Date(currentUser.createdAt?.toDate?.() || currentUser.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                          : 'Unknown',
                        icon: Award
                      },
                      {
                        label: 'Account Type',
                        value: currentUser?.isAdmin ? 'Administrator' : 'Standard User',
                        icon: User
                      },
                    ].map(({ label, value, icon: Icon }) => (
                      <div key={label} className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">{label}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-800 font-mono truncate max-w-[160px]">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════════════ APPEARANCE ════════════ */}
          {activeSection === 'appearance' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Palette} title="Appearance" desc="Customize the look and feel of Vibely Books" />

              {/* Theme */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Theme</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Light', icon: Sun, value: false, iconColor: 'text-amber-500' },
                    { label: 'Dark',  icon: Moon, value: true, iconColor: 'text-indigo-500' },
                    { label: 'System', icon: Monitor, value: null, iconColor: 'text-gray-500' },
                  ].map(({ label, icon: Icon, value, iconColor }) => (
                    <button
                      key={label}
                      onClick={() => {
                        if (value === null) {
                          setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
                        } else {
                          setDarkMode(value);
                        }
                      }}
                      className={`p-4 rounded-xl border-2 transition text-center ${
                        (value === null ? false : darkMode === value)
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mx-auto mb-2 ${iconColor}`} />
                      <p className="text-xs font-medium text-gray-700">{label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent Color */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Accent Color</p>
                <div className="flex gap-3 flex-wrap">
                  {accentColors.map(color => (
                    <button
                      key={color.name}
                      onClick={() => setAccentColor(color.name)}
                      title={color.label}
                      className="flex flex-col items-center gap-1.5"
                    >
                      <div
                        style={{ backgroundColor: color.hex }}
                        className={`w-9 h-9 rounded-full transition-all ${
                          accentColor === color.name
                            ? 'ring-2 ring-offset-2 ring-gray-500 scale-110'
                            : 'hover:scale-105'
                        }`}
                      />
                      <span className="text-xs text-gray-500">{color.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Text Size</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'small',  label: 'Small',  preview: 'text-sm'  },
                    { value: 'medium', label: 'Medium', preview: 'text-base' },
                    { value: 'large',  label: 'Large',  preview: 'text-lg'  },
                  ].map(({ value, label, preview }) => (
                    <button
                      key={value}
                      onClick={() => setFontSize(value)}
                      className={`p-4 rounded-xl border-2 transition text-center ${
                        fontSize === value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className={`font-semibold text-gray-800 ${preview}`}>Aa</p>
                      <p className="text-xs text-gray-500 mt-1">{label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ════════════ NOTIFICATIONS ════════════ */}
          {activeSection === 'notifications' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Bell} title="Notifications" desc="Control which notifications you receive" />

              <div className="divide-y divide-gray-100">
                {[
                  { key: 'bookClaimed',        label: 'Book Claimed',        desc: 'When a reviewer claims your book',       icon: BookOpen    },
                  { key: 'reviewApproved',     label: 'Review Approved',     desc: 'When your review is approved',           icon: CheckCircle },
                  { key: 'reviewRejected',     label: 'Review Rejected',     desc: 'When your review needs revision',        icon: AlertTriangle },
                  { key: 'lowCoins',           label: 'Low Coin Balance',    desc: 'When your balance drops below 50 coins', icon: Coins       },
                  { key: 'weeklyLimit',        label: 'Weekly Limit Reached', desc: 'When you hit your weekly claim limit',  icon: BarChart2   },
                  { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive important updates via email',    icon: Bell        },
                ].map(({ key, label, desc, icon: Icon }) => (
                  <SettingRow key={key} icon={Icon} label={label} desc={desc}>
                    <Toggle
                      value={notifications[key]}
                      onChange={() => setNotifications(prev => ({ ...prev, [key]: !prev[key] }))}
                    />
                  </SettingRow>
                ))}
              </div>

              <SaveButton onClick={handleSaveNotifications} saving={saving} label="Save Notification Settings" />
            </div>
          )}

          {/* ════════════ SUBSCRIPTION ════════════ */}
          {activeSection === 'subscription' && (
            <div className="space-y-4">

              {/* Coin Balance */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <SectionHeader icon={Coins} title="Coin Balance" desc="Your current available coins" />
                <div className="flex items-center gap-5 p-5 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                  <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Coins className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-amber-700">{currentUser?.coins?.toLocaleString() || 0}</p>
                    <p className="text-sm text-amber-600 mt-0.5">Available Coins</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">Earn coins by reviewing books. Use them to pay reviewers for your books.</p>
              </div>

              {/* Review Plan */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Review Plan</h3>
                      <p className="text-xs text-gray-500">Submit unlimited book reviews</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSubscriptionModal(true)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      currentUser?.reviewSubscription?.active
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {currentUser?.reviewSubscription?.active ? 'Manage' : 'Subscribe'}
                  </button>
                </div>
                <div className={`px-4 py-3 rounded-lg border text-sm ${currentUser?.reviewSubscription?.active ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                  {currentUser?.reviewSubscription?.active ? (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{currentUser.reviewSubscription.plan?.toUpperCase()} — Active</span>
                      <span className="text-xs">Expires {new Date(currentUser.reviewSubscription.expiresAt).toLocaleDateString()}</span>
                    </div>
                  ) : (
                    <span>{Math.max(0, 5 - (currentUser?.reviewsSubmitted || 0))} free reviews remaining</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ════════════ PRIVACY ════════════ */}
          {activeSection === 'privacy' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Eye} title="Privacy" desc="Control your visibility and data sharing" />

              <div className="divide-y divide-gray-100 mb-6">
                {[
                  { key: 'profilePublic',    label: 'Public Profile',      desc: 'Allow other users to view your profile',   icon: User  },
                  { key: 'showReviewHistory',label: 'Show Review History', desc: 'Let others see the reviews you have written', icon: BookOpen },
                  { key: 'showCoinBalance',  label: 'Show Coin Balance',   desc: 'Display your coin balance on your profile', icon: Coins },
                ].map(({ key, label, desc, icon: Icon }) => (
                  <SettingRow key={key} icon={Icon} label={label} desc={desc}>
                    <Toggle
                      value={privacy[key]}
                      onChange={() => setPrivacy(prev => ({ ...prev, [key]: !prev[key] }))}
                    />
                  </SettingRow>
                ))}
              </div>

              {/* Export */}
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Database className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Export My Data</p>
                      <p className="text-xs text-gray-500">Download a copy of your account data</p>
                    </div>
                  </div>
                  <button
                    onClick={handleExportData}
                    className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-medium flex-shrink-0"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>

              <SaveButton onClick={handleSavePrivacy} saving={saving} label="Save Privacy Settings" />
            </div>
          )}

          {/* ════════════ AFFILIATE ════════════ */}
          {activeSection === 'affiliate' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Share2} title="Affiliate Program" desc="Earn commissions by referring new users" />

              {currentUser?.isAffiliate ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800 text-sm">Active Affiliate</p>
                      <p className="text-xs text-green-600">Your referral link is live and tracking conversions</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your Referral Link</label>
                    <div className="flex gap-2">
                      <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-lg">
                        <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 font-mono truncate">
                          {window.location.origin}?ref={currentUser.referralCode}
                        </span>
                      </div>
                      <button
                        onClick={handleCopyReferral}
                        className="flex-shrink-0 flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl border border-gray-200 text-center bg-gray-50">
                      <p className="text-2xl font-bold text-gray-900">$0.00</p>
                      <p className="text-xs text-gray-500 mt-1">Total Earned</p>
                    </div>
                    <div className="p-4 rounded-xl border border-gray-200 text-center bg-gray-50">
                      <p className="text-2xl font-bold text-gray-900">0</p>
                      <p className="text-xs text-gray-500 mt-1">Total Referrals</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                    <Share2 className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Join the Affiliate Program</h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                    Refer authors and reviewers to Vibely Books and earn recurring commissions.
                  </p>
                  <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto mb-6 text-left">
                    {[
                      { label: 'Monthly referral', value: '$5.00' },
                      { label: 'Lifetime recurring', value: '10%' },
                      { label: 'Yearly referral', value: '$30.00' },
                      { label: 'Min. withdrawal', value: '$50.00' },
                    ].map(({ label, value }) => (
                      <div key={label} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-base font-bold text-gray-900">{value}</p>
                        <p className="text-xs text-gray-500">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════════ PREFERENCES ════════════ */}
          {activeSection === 'preferences' && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Sliders} title="Preferences" desc="Customize your browsing and review experience" />

              <div className="space-y-4">
                {[
                  {
                    label: 'Default Marketplace',
                    desc: 'Filter books by Amazon marketplace by default',
                    key: 'defaultMarketplace',
                    options: [
                      { value: 'US', label: 'United States (amazon.com)' },
                      { value: 'UK', label: 'United Kingdom (amazon.co.uk)' },
                      { value: 'CA', label: 'Canada (amazon.ca)' },
                      { value: 'AU', label: 'Australia (amazon.com.au)' },
                      { value: 'ALL', label: 'All Marketplaces' },
                    ]
                  },
                  {
                    label: 'Default Genre',
                    desc: 'Pre-select a genre when browsing books',
                    key: 'defaultGenre',
                    options: [
                      { value: 'All', label: 'All Genres' },
                      { value: 'Fiction', label: 'Fiction' },
                      { value: 'Non-Fiction', label: 'Non-Fiction' },
                      { value: 'Romance', label: 'Romance' },
                      { value: 'Mystery, Thriller & Suspense', label: 'Mystery & Thriller' },
                      { value: 'Science Fiction', label: 'Science Fiction' },
                      { value: 'Fantasy', label: 'Fantasy' },
                      { value: 'Self-Help', label: 'Self-Help' },
                    ]
                  },
                  {
                    label: 'Review Deadline Reminder',
                    desc: 'Remind me this many days before a review is due',
                    key: 'reminderDays',
                    options: [
                      { value: 1, label: '1 day before' },
                      { value: 2, label: '2 days before' },
                      { value: 3, label: '3 days before' },
                      { value: 5, label: '5 days before' },
                    ]
                  },
                  {
                    label: 'Language',
                    desc: 'Interface language preference',
                    key: 'language',
                    options: [
                      { value: 'English', label: 'English' },
                      { value: 'Spanish', label: 'Spanish' },
                      { value: 'French', label: 'French' },
                      { value: 'German', label: 'German' },
                      { value: 'Portuguese', label: 'Portuguese' },
                    ]
                  },
                ].map(({ label, desc, key, options }) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{label}</label>
                    <p className="text-xs text-gray-400 mb-2">{desc}</p>
                    <select
                      value={preferences[key]}
                      onChange={e => setPreferences(p => ({ ...p, [key]: key === 'reminderDays' ? parseInt(e.target.value) : e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      {options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <SaveButton onClick={handleSavePreferences} saving={saving} label="Save Preferences" />
            </div>
          )}

          {/* ════════════ DANGER ZONE ════════════ */}
          {activeSection === 'danger' && (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <h2 className="font-bold text-red-800">Danger Zone</h2>
                    <p className="text-sm text-red-600">These actions cannot be undone. Proceed with caution.</p>
                  </div>
                </div>
              </div>

              {[
                {
                  icon: LogOut,
                  title: 'Sign Out',
                  desc: 'Sign out of your account on this device',
                  buttonLabel: 'Sign Out',
                  buttonStyle: 'bg-gray-600 hover:bg-gray-700 text-white',
                  action: async () => {
                    await signOutUser();
                    setView('login');
                  }
                },
                {
                  icon: RefreshCw,
                  title: 'Deactivate Account',
                  desc: 'Temporarily disable your account. You can reactivate by logging back in.',
                  buttonLabel: 'Deactivate',
                  buttonStyle: 'bg-orange-500 hover:bg-orange-600 text-white',
                  action: async () => {
                    if (window.confirm('Are you sure you want to deactivate your account?')) {
                      await updateUser(currentUser.id, { deactivated: true });
                      await signOutUser();
                      setView('login');
                    }
                  }
                },
                {
                  icon: UserX,
                  title: 'Delete Account',
                  desc: 'Permanently remove your account, books, reviews, and all data. This action is irreversible.',
                  buttonLabel: 'Delete Account',
                  buttonStyle: 'bg-red-600 hover:bg-red-700 text-white',
                  action: () => {
                    alert('To permanently delete your account, please contact support:\nsupport@vibelybooks.live');
                  }
                },
              ].map(({ icon: Icon, title, desc, buttonLabel, buttonStyle, action }) => (
                <div key={title} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                    <button
                      onClick={action}
                      className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${buttonStyle} sm:flex-shrink-0`}
                    >
                      <Icon className="w-4 h-4" />
                      {buttonLabel}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SettingsPage;